<template>
  <div class="o-focus o-tabs__tab--fixed-head">
    <div class="o-panel__header" v-my-fixed-title>
      <h3 class="o-panel__title">Delivering a reliable electricity supply</h3>
    </div>

    <my-slider ref="slider">
      <!-- SLIDE -->
      <my-slide>
        <div class="o-panel__body">
          <div class="o-focus__intro">
            <p>It is our job to keep the lights on safely and reliably for all our customers and, as long as everyone is safe and well, we see that as our purpose. As we invest to achieve technical improvements, power cuts have become increasingly rare. These days, when they do happen, they tend not to last long. External factors, particularly severe weather or vehicle accidents can still damage cables causing power cuts. We work hard to keep bringing down both the number and duration of power cuts. A key contributor to these improvements is the Automatic Power Restoration System (APRS). This innovation identifies and isolates the location of a fault and re-routes power on the network so that many fewer customers are affected by the fault for more than a couple of minutes. Once the fault is isolated, we can send an engineer to fix it. That way, only those customers who are directly affected by the breakdown are inconvenienced by a power cut. APRS is a pioneering approach to dealing with faults that is leading the industry.</p>
          </div>

          <div class="o-link__btn-group">
            <my-download-button :section="'focus-network-reliability'"></my-download-button>
            <my-video-button :video-code="'238073562'"></my-video-button>
          </div>

        </div>
      </my-slide>

      <!-- SLIDE -->
      <my-slide>
        <div class="o-panel__body o-panel__body--full">

          <p><strong>Customer Interruptions (CI)</strong>: the number of customers interrupted per 100 customers on our network.</p>

          <!-- Accordian tabs -->
          <my-accordians @tab-changed="tabHasChanged">

            <my-accordian :title="'UK Power Networks Average'">
              <div class="o-media--focus">
                <div class="o-media__data">
                  <figure class="o-media__chart o-chart">
                    <figcaption class="o-chart__caption">UK Power Networks Average</figcaption>
                    <my-chart :color="['turq']" :file="'interuptions-average'"></my-chart>
                    <p class="u-footnote">Figures are a weighted average of the three licence areas and exclude exceptional events</p>
                  </figure>
                </div>
                <div class="o-media__text">

                  <figure class="o-media__kpi o-kpi">
                    <span class="o-kpi__figure">39.9 CI</span>
                    <figcaption class="o-kpi__caption">Customer interuptions in 2016/17</figcaption>
                  </figure>
                  <p>This means that customers in 2010/11 were interrupted on average once every 18 months. Our performance improvement has resulted in customers now seeing an interruption on average once every two and a half years.</p>
                </div>
              </div>
            </my-accordian>

            <my-accordian :title="'East'">
              <div class="o-media--focus">
                <div class="o-media__data">
                  <figure class="o-media__chart o-chart">
                    <figcaption class="o-chart__caption">East</figcaption>
                    <my-chart :color="['turq']" :file="'interuptions-east'"></my-chart>
                    <p class="u-footnote">Figures exclude exceptional events</p>
                  </figure>
                </div>
                <div class="o-media__text">

                  <figure class="o-media__kpi o-kpi">
                    <span class="o-kpi__figure">49.5 CI</span>
                    <figcaption class="o-kpi__caption">Customer interuptions in 2016/17</figcaption>
                  </figure>
                  <p>For our Eastern network, customers in 2010/11 were interrupted on average once every 14 months. Our performance improvement has resulted in customers now seeing an interruption on average once every two years.</p>
                </div>
              </div>
            </my-accordian>

            <my-accordian :title="'London'">
              <div class="o-media--focus">
                <div class="o-media__data">
                  <figure class="o-media__chart o-chart">
                    <figcaption class="o-chart__caption">London</figcaption>
                    <my-chart :color="['turq']" :file="'interuptions-london'"></my-chart>
                    <p class="u-footnote">Figures exclude exceptional events</p>
                  </figure>
                </div>
                <div class="o-media__text">

                  <figure class="o-media__kpi o-kpi">
                    <span class="o-kpi__figure">17.2 CI</span>
                    <figcaption class="o-kpi__caption">Customer interuptions in 2016/17</figcaption>
                  </figure>
                  <p>This means that customers in 2010/11 were interrupted on average once every four years. Our performance improvement has resulted in customers now seeing an interruption on average once every five and half years.</p>
                </div>
              </div>
            </my-accordian>

            <my-accordian :title="'South East'">
              <div class="o-media--focus">
                <div class="o-media__data">
                  <figure class="o-media__chart o-chart">
                    <figcaption class="o-chart__caption">South East</figcaption>
                    <my-chart :color="['turq']" :file="'interuptions-south-east'"></my-chart>
                    <p class="u-footnote">Figures exclude exceptional events</p>
                  </figure>
                </div>
                <div class="o-media__text">

                  <figure class="o-media__kpi o-kpi">
                    <span class="o-kpi__figure">47.8 CI</span>
                    <figcaption class="o-kpi__caption">Customer interuptions in 2016/17</figcaption>
                  </figure>
                  <p>This means that customers in 2010/11 were interrupted on average once every 16 months. Our performance improvement has resulted in customers now seeing an interruption on average once every two years.</p>
                </div>
              </div>
            </my-accordian>

          </my-accordians>

        </div>
      </my-slide>


      <!-- SLIDE -->
      <my-slide>
        <div class="o-panel__body o-panel__body--full">

          <p><strong>Customer Minutes Lost (CML)</strong>: the average length of time customers are without power, for power cuts lasting three minutes or longer.</p>

          <!-- Accordian tabs -->
          <my-accordians @tab-changed="tabHasChanged">

            <my-accordian :title="'UK Power Networks Average'">
              <div class="o-media--focus">
                <div class="o-media__data">
                  <figure class="o-media__chart o-chart">
                    <figcaption class="o-chart__caption">UK Power Networks Average</figcaption>
                    <my-chart :color="['turq']" :file="'minutes-average'"></my-chart>
                    <p class="u-footnote">Figures are a weighted average of the three licence areas and exclude exceptional events</p>
                  </figure>
                </div>
                <div class="o-media__text">

                  <figure class="o-media__kpi o-kpi">
                    <span class="o-kpi__figure">32.6 CML</span>
                    <figcaption class="o-kpi__caption">Customer minutes lost in 2016/17</figcaption>
                  </figure>
                  <p>On average, a customer connected to our network will be off supply for just over half an hour per year. This is a 49% improvement over 2010/11, when the average duration was 64 minutes.</p>
                </div>
              </div>
            </my-accordian>

            <my-accordian :title="'East'">
              <div class="o-media--focus">
                <div class="o-media__data">
                  <figure class="o-media__chart o-chart">
                    <figcaption class="o-chart__caption">East</figcaption>
                    <my-chart :color="['turq']" :file="'minutes-east'"></my-chart>
                    <p class="u-footnote">Figures exclude exceptional events</p>
                  </figure>
                </div>
                <div class="o-media__text">

                  <figure class="o-media__kpi o-kpi">
                    <span class="o-kpi__figure">39.3 CML</span>
                    <figcaption class="o-kpi__caption">Customer minutes lost in 2016/17</figcaption>
                  </figure>
                  <p>On average, a customer connected to our Eastern network will be off supply for 39 minutes per year. This is a 46% improvement on 2010/11, when the average duration was 72 minutes.</p>
                </div>
              </div>
            </my-accordian>

            <my-accordian :title="'London'">
              <div class="o-media--focus">
                <div class="o-media__data">
                  <figure class="o-media__chart o-chart">
                    <figcaption class="o-chart__caption">London</figcaption>
                    <my-chart :color="['turq']" :file="'minutes-london'"></my-chart>
                    <p class="u-footnote">Figures exclude exceptional events</p>
                  </figure>
                </div>
                <div class="o-media__text">

                  <figure class="o-media__kpi o-kpi">
                    <span class="o-kpi__figure">19.8 CML</span>
                    <figcaption class="o-kpi__caption">Customer minutes lost in 2016/17</figcaption>
                  </figure>
                  <p>On average, a customer connected to our London network will be off supply for 19 minutes per year. This is a 53% improvement on 2010/11, when the average duration was 42 minutes.</p>
                </div>
              </div>
            </my-accordian>

            <my-accordian :title="'South East'">
              <div class="o-media--focus">
                <div class="o-media__data">
                  <figure class="o-media__chart o-chart">
                    <figcaption class="o-chart__caption">South East</figcaption>
                    <my-chart :color="['turq']" :file="'minutes-south-east'"></my-chart>
                    <p class="u-footnote">Figures exclude exceptional events</p>
                  </figure>
                </div>
                <div class="o-media__text">

                  <figure class="o-media__kpi o-kpi">
                    <span class="o-kpi__figure">35.1 CML</span>
                    <figcaption class="o-kpi__caption">Customer minutes lost in 2016/17</figcaption>
                  </figure>
                  <p>On average, a customer connected to our South Eastern network will be off supply for 35 minutes per year. This is a 52% improvement on 2010/11, when the average duration was 73 minutes.</p>
                </div>
              </div>
            </my-accordian>

          </my-accordians>

        </div>
      </my-slide>

    </my-slider>

  </div>
</template>

<script>
import MyChart from '@/components/standard/my-chart'
import { MyAccordians, MyAccordian } from '@/components/standard/my-accordian'
import MyDownloadButton from '@/components/standard/my-download-button'
import MyVideoButton from '@/components/standard/my-video-button'
import { MySlider, MySlide } from '@/components/standard/my-slider'
import { MyFixedTitle } from '@/directives/my-fixed-title.js'

export default {
  name: 'my-focus-network',
  components: {
    MyChart,
    MyAccordians,
    MyAccordian,
    MyDownloadButton,
    MyVideoButton,
    MySlider,
    MySlide
  },
  directives: {
    MyFixedTitle
  },
  methods: {
    tabHasChanged () {
      this.$refs.slider.resizeFlkty()
    }
  }
}
</script>



// WEBPACK FOOTER //
// MyFocusNetwork.vue?4e5b1736